import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import {
  ButtonFormikTab,
  CustomButton,
  FormTextField,
  ReactSelectForm,
} from '../../../../../components';

import { SettingBox } from '../../../common/components/SettingBox';
import {
  boolOption,
  initialData,
  isDiscountOption,
  ratingOption,
  stateClassOption,
  ticketSalesOption,
  tripleOption,
} from '../../../../../constant/setting';
import { AddIcon } from '../../../../../assets/icons/AddIcon';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { openModal } from '../../../../../features/modal/modalSlice';
import { ModalAction, ModalName } from '../../../../../types/modal';
import { deleteMember } from '../../../../../features/setting/settingSlice';
import { settingTagTypeConverter } from '../../../../../utils/settingTagTypeConverter';
import { updateTournamentInfo } from '../../../../../features/tournament/actionCreators';
import { MemberTournamentCard } from '../../../../../components';
import { TagIdsEnum } from '../../../../../constant/navigation';
import { keywordsTypes } from '../../../../adminConfiguration/adminCounfiguration';

import './SettingTag.scss';
import { useParams } from 'react-router-dom';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { InfoIcon } from '../../../../../assets/icons/InfoIcon';
import { getBool } from '../../../../../utils/typeConverters';
import { useGetOrganizationTours } from '../../../../../generated/orval/admin';
import { Notification } from '../../../../../hooks/useNotification';
import { GroupStandingsTieBreakerEnum, TypeError } from '../../../../../types/enums';
import { buildCurrencyOptions } from '../../../../../utils/currencyOptions';
import { GroupStandingsTieBreakerModalControl } from '../../../common/components/GroupStandingsTieBreakerEditor';
import { SubmitValidationNotifier } from '../../../../../components/form/SubmitValidationNotifier';
import {
  DEFAULT_TEAM_MATCHUP_SCORING,
  getGroupStandingsInheritanceLabelKey,
  getTeamMatchupScoringInheritanceLabelKey,
  hasRequiredGroupStandingsTieBreakers,
  requiredGroupStandingsTieBreakersErrorKey,
  resolveGroupStandingsTieBreakerResolution,
  TeamMatchupScoring,
} from '../../../../../utils/groupStandingsTieBreakers';
import { useGetCurrentOrganization } from '../../../../../features/organizations/organizationsSelectors';

export interface ISettingValues {
  keywordSport: number;
  tour: string;
  keywordTournamentType: number;
  status: string;
  showOnPublicPage: string;
  liveStreaming: string;
  liveStreamingLink: string;
  isRating: string;
  ratingType: string;
  isRanking: string;
  rankingType: string;
  isCanRecordScores: string;
  // matchPoints: number;
  stateOfClass: string;
  keywordBall: number;
  keywordSurface: number;
  isTicketSales: string;
  isShowSalesButton: string;
  minPlayers: number;
  maxPlayers: number;
  minTeamRoster: number;
  maxTeamRoster: number;
  isPaidClass: boolean;
  price: number;
  vat: number;
  useVat: boolean;
  isDiscount: boolean | string;
  discount1: number;
  discount2: number;
  discount3: number;
  currency: string;
  isDupr: boolean;
  requiresLicense: boolean;
  groupStandingsTieBreakers: GroupStandingsTieBreakerEnum[];
  useInheritedGroupStandingsTieBreakers: boolean;
  teamMatchupScoring: TeamMatchupScoring;
  useInheritedTeamMatchupScoring: boolean;
}

export type SettingValues = ISettingValues;

const resolveInheritedTournamentTieBreakers = (
  tourTieBreakers?: GroupStandingsTieBreakerEnum[] | null,
  organizationTieBreakers?: GroupStandingsTieBreakerEnum[] | null,
): GroupStandingsTieBreakerEnum[] =>
  resolveGroupStandingsTieBreakerResolution({
    tourTieBreakers: tourTieBreakers ?? null,
    organizationTieBreakers: organizationTieBreakers ?? null,
  }).effective;

export const SettingTag = () => {
  const { t, i18n } = useTranslation();
  const prefix = 'creativeDetail-';
  const buttonPrefix = 'button-';
  const prefixValidation = 'validation-';

  const dispatch = useAppDispatch();
  const { data } = useAppSelector((state) => state.tournament);
  const { listKeywords } = useAppSelector((state) => state.settings);

  const officials = useAppSelector((state) => state.settings.officialsMembers);
  const { communityId } = useParams<{ communityId: string }>();
  const [showDiscountInfo, setShowDiscountInfo] = useState(false);
  const [tieBreakerOpenSignal, setTieBreakerOpenSignal] = useState(0);
  const handleTieBreakerInvalid = useCallback(() => {
    setTieBreakerOpenSignal((count) => count + 1);
  }, []);
  const currentOrganization = useGetCurrentOrganization();

  // Fetch tours accessible by this organization
  const { data: organizationToursData } = useGetOrganizationTours(
    communityId ? parseInt(communityId, 10) : 0,
    { query: { enabled: !!communityId } }
  );
  const organizationTours = organizationToursData?.items || [];

  const sportOptions = listKeywords
    .filter(({ type }) => type === keywordsTypes.SPORT)
    .map(({ id, name }) => ({ value: id, label: name }));
  const tournamentTypeOptions = listKeywords
    .filter(({ type }) => type === keywordsTypes.TOURNAMENT_TYPE)
    .map(({ id, name }) => ({ value: id, label: name }));
  const surfaceOption = listKeywords
    .filter(({ type }) => type === keywordsTypes.SURFACE)
    .map(({ id, name }) => ({ value: id, label: name }));
  const ballOption = listKeywords
    .filter(({ type }) => type === keywordsTypes.BALL)
    .map(({ id, name }) => ({ value: id, label: name }));
  const currencyOptions = buildCurrencyOptions(i18n.language);
  const initialSelectedTour = organizationTours.find(
    (tour) => tour.id === Number(data?.settings?.tour || 0),
  ) as { groupStandingsTieBreakers?: GroupStandingsTieBreakerEnum[] | null } | undefined;
  const inheritedTournamentTieBreakers = resolveInheritedTournamentTieBreakers(
    initialSelectedTour?.groupStandingsTieBreakers ?? null,
    currentOrganization?.groupStandingsTieBreakers ?? null,
  );

  const formikInitialState =
    data?.settings && data?.classSettings
      ? ({
          keywordSport: data.settings.keywordSport,
          tour: data.settings.tour ? data.settings.tour.toString() : '',
          keywordTournamentType: data.settings.keywordTournamentType,
          status: t(tripleOption[data.settings.status]),
          showOnPublicPage: data.settings.showOnPublicPage.toString(),
          liveStreaming: data.settings.liveStreaming.toString(),
          liveStreamingLink: data.settings.liveStreamingLink,
          isRating: data.settings.isRating.toString(),
          ratingType: data.settings.ratingType.toString(),
          isCanRecordScores: data.settings.isCanRecordScores.toString(),
          // matchPoints: data.settings.matchPoints,
          stateOfClass: t(stateClassOption[Number(data.classSettings.stateOfClass)]),
          keywordBall: data.classSettings.keywordBall,
          keywordSurface: data.classSettings.keywordSurface,
          isTicketSales: t(ticketSalesOption[Number(data.classSettings.isTicketSales)]),
          isShowSalesButton: data.classSettings.isShowSalesButton.toString(),
          minPlayers: data.classSettings.minPlayers,
          maxPlayers: data.classSettings.maxPlayers,
          minTeamRoster: data.classSettings.minTeamRoster ?? 2,
          maxTeamRoster: data.classSettings.maxTeamRoster ?? 4,
          isPaidClass: data.classSettings.price > 0,
          price: data.classSettings.price,
          vat: data.classSettings.vat,
          useVat: data.classSettings.vat !== 0,
          isDiscount: data.isDiscount.toString(),
          discount1: data.discount1,
          discount2: data.discount2,
          discount3: data.discount3,
          currency: data.currency || '',
          isDupr: data.classSettings.isDupr || false,
          requiresLicense: data.classSettings.requiresLicense || false,
          groupStandingsTieBreakers: data.settings.groupStandingsTieBreakers?.length
            ? data.settings.groupStandingsTieBreakers
            : inheritedTournamentTieBreakers,
          useInheritedGroupStandingsTieBreakers:
            !data.settings.groupStandingsTieBreakers?.length,
          teamMatchupScoring:
            (data.settings as { teamMatchupScoring?: TeamMatchupScoring | null }).teamMatchupScoring ??
            DEFAULT_TEAM_MATCHUP_SCORING,
          useInheritedTeamMatchupScoring:
            !(data.settings as { teamMatchupScoring?: TeamMatchupScoring | null }).teamMatchupScoring,
        } as ISettingValues)
      : {
          ...initialData(t),
          currency: data?.currency || '',
          groupStandingsTieBreakers: inheritedTournamentTieBreakers,
          useInheritedGroupStandingsTieBreakers: true,
          teamMatchupScoring: DEFAULT_TEAM_MATCHUP_SCORING,
          useInheritedTeamMatchupScoring: true,
        };

  const SettingSchema = Yup.object().shape({
    currency: Yup.string().required(t(prefixValidation + 'required')),
    liveStreaming: Yup.boolean(),
    liveStreamingLink: Yup.string().when('liveStreaming', {
      is: (v: unknown) => getBool(v),
      then: Yup.string()
        .required(t(prefixValidation + 'required'))
        .url(t(prefixValidation + 'url')),
    }),
    // matchPoints: Yup.number()
    //     .when("keywordTournamentType", {
    //         is: (tournamentType: number) => tournamentType === 14,
    //         then: Yup.number().required(t(prefixValidation + 'required'))
    //         .typeError(t(prefixValidation + 'mustBeANumber'))
    //         .positive(t(prefixValidation + 'positive')),
    //     }),
    minPlayers: Yup.number()
      .required(t(prefixValidation + 'required'))
      .typeError(t(prefixValidation + 'mustBeANumber'))
      .min(0, t(prefixValidation + 'minZero')),
    maxPlayers: Yup.number()
      .required(t(prefixValidation + 'required'))
      .typeError(t(prefixValidation + 'mustBeANumber'))
      .positive(t(prefixValidation + 'positive')),
    minTeamRoster: Yup.number()
      .required(t(prefixValidation + 'required'))
      .typeError(t(prefixValidation + 'mustBeANumber'))
      .min(1, t(prefixValidation + 'positive'))
      .max(20, t(prefixValidation + 'maxRosterLimit')),
    maxTeamRoster: Yup.number()
      .required(t(prefixValidation + 'required'))
      .typeError(t(prefixValidation + 'mustBeANumber'))
      .min(1, t(prefixValidation + 'positive'))
      .max(20, t(prefixValidation + 'maxRosterLimit'))
      .test('gte-min-roster', t(prefixValidation + 'maxGteMinRoster'), function (value) {
        const min = Number(this.parent.minTeamRoster);
        return value == null || !Number.isFinite(min) || Number(value) >= min;
      }),
    price: Yup.number().when('isPaidClass', {
      is: (v: unknown) => getBool(v),
      then: Yup.number()
        .typeError(t(prefixValidation + 'mustBeANumber'))
        .required(t(prefixValidation + 'required'))
        .positive(t(prefixValidation + 'positive')),
    }),
    vat: Yup.number().when('isPaidClass', {
      is: (v: unknown) => getBool(v),
      then: Yup.number()
        .typeError(t(prefixValidation + 'mustBeANumber'))
        .required(t(prefixValidation + 'required'))
        .min(0, t(prefixValidation + 'positive')),
    }),
    discount1: Yup.number().when('isDiscount', {
      is: (v: unknown) => getBool(v),
      then: Yup.number()
        .typeError(t(prefixValidation + 'mustBeANumber'))
        .required(t(prefixValidation + 'required'))
        .moreThan(-1, t(prefixValidation + 'positive'))
        .lessThan(100)
        .nullable(true),
    }),
    discount2: Yup.number().when('isDiscount', {
      is: (v: unknown) => getBool(v),
      then: Yup.number()
        .typeError(t(prefixValidation + 'mustBeANumber'))
        .required(t(prefixValidation + 'required'))
        .moreThan(-1, t(prefixValidation + 'positive'))
        .lessThan(100)
        .nullable(true),
    }),
    discount3: Yup.number().when('isDiscount', {
      is: (v: unknown) => getBool(v),
      then: Yup.number()
        .typeError(t(prefixValidation + 'mustBeANumber'))
        .required(t(prefixValidation + 'required'))
        .moreThan(-1, t(prefixValidation + 'positive'))
        .lessThan(100)
        .nullable(true),
    }),
    groupStandingsTieBreakers: Yup.array().when(
      'useInheritedGroupStandingsTieBreakers',
      {
        is: false,
        then: Yup.array().test('required-core', function (value) {
          const list = (value as GroupStandingsTieBreakerEnum[] | undefined) ?? [];
          if (hasRequiredGroupStandingsTieBreakers(list)) {
            return true;
          }
          return this.createError({
            message: t(requiredGroupStandingsTieBreakersErrorKey(list)),
          });
        }),
      },
    ),
  });

  const handleDeleteMember = useCallback(
    (id: number) => {
      dispatch(deleteMember(id));
    },
    [dispatch]
  );

  const handleOpenModal = useCallback(() => {
    dispatch(
      openModal({
        currentModal: ModalAction.ADD_MEMBER,
        modalOpen: true,
        data: {
          title: 'Add tournament officials: ',
          modalName: ModalName.ADD_TOURNAMENT_MEMBER,
          selected: officials,
        },
      })
    );
  }, [dispatch, officials]);

  return (
    <div className="px-2">
      <Formik
        enableReinitialize={true}
        validateOnMount={true}
        validationSchema={SettingSchema}
        initialValues={formikInitialState}
        onSubmit={(values: ISettingValues) => {
          data &&
            dispatch(
              updateTournamentInfo(data.id, +communityId, {
                ...settingTagTypeConverter(values, t),
                officials,
              })
            );
        }}
      >
        {({ values, setFieldValue }) => {
          const selectedTour = organizationTours.find(
            (tour) => tour.id === Number(values.tour || 0),
          ) as {
            groupStandingsTieBreakers?: GroupStandingsTieBreakerEnum[] | null;
            teamMatchupScoring?: TeamMatchupScoring | null;
          } | undefined;
          const liveResolution = resolveGroupStandingsTieBreakerResolution({
            tournamentTieBreakers: values.useInheritedGroupStandingsTieBreakers
              ? null
              : values.groupStandingsTieBreakers,
            tourTieBreakers: selectedTour?.groupStandingsTieBreakers ?? null,
            organizationTieBreakers:
              currentOrganization?.groupStandingsTieBreakers ?? null,
            tournamentTeamMatchupScoring: values.useInheritedTeamMatchupScoring
              ? null
              : values.teamMatchupScoring,
            tourTeamMatchupScoring: selectedTour?.teamMatchupScoring ?? null,
            organizationTeamMatchupScoring: currentOrganization?.teamMatchupScoring ?? null,
          });
          const tournamentResolution = liveResolution;

          return (
          <Form autoComplete="off" id={TagIdsEnum.SETTINGS}>
            <SubmitValidationNotifier onTieBreakerInvalid={handleTieBreakerInvalid} />
            <div className="row">
              <SettingBox title="Sport">
                <ReactSelectForm labelPdd="pb-0" options={sportOptions} name="keywordSport" />
              </SettingBox>
              <SettingBox title="Tour">
                <ReactSelectForm
                  labelPdd="pb-0"
                  options={[
                    { value: '', label: t(prefix + 'noTour') },
                    ...organizationTours.map((tour) => {
                      return { value: tour.id.toString(), label: tour.description || tour.name };
                    }),
                  ]}
                  name="tour"
                />
              </SettingBox>
              <SettingBox title="TournamentType">
                <ReactSelectForm
                  labelPdd="pb-0"
                  options={tournamentTypeOptions}
                  name="keywordTournamentType"
                />
              </SettingBox>
              <SettingBox title="Status">
                <ButtonFormikTab
                  currValue={t(values.status)}
                  items={tripleOption.map(t) as string[]}
                  name="status"
                />
              </SettingBox>
              <div className="d-block d-lg-flex align-items-center my-3">
                <div className="col-lg-4 col pb-0 pb-lg-3 pb-lg-0 ps-0">
                  <div className="d-flex align-items-center gap-2">
                    <span>{t(prefix + 'hidden')}</span>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          {t(prefix + 'hiddenTooltip', { link: data?.id ? `raqt.com/tournament/${data.id}` : 'raqt.com/tournament/{id}' })}
                        </Tooltip>
                      }
                    >
                      <Button variant="link" className="p-0" aria-label={t(prefix + 'hidden')}>
                        <InfoIcon width={24} height={24} />
                      </Button>
                    </OverlayTrigger>
                    {values.showOnPublicPage === 'false' && data?.id && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(`https://raqt.com/tournament/${data.id}`);
                            Notification(TypeError.success, t(prefix + 'linkCopied'));
                          } catch {
                            Notification(TypeError.error, t(prefix + 'linkCopyFailed'));
                          }
                        }}
                      >
                        {t(prefix + 'copyDirectLink')}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="col px-0">
                  <div className="settingInputContainer">
                    <ButtonFormikTab
                      currValue={values.showOnPublicPage}
                      items={[
                        { label: t('toggle-yes'), value: 'false' },
                        { label: t('toggle-no'), value: 'true' },
                      ]}
                      name="showOnPublicPage"
                    />
                  </div>
                </div>
              </div>
              <SettingBox title="Currency">
                <ReactSelectForm
                  labelPdd="pb-0"
                  options={currencyOptions}
                  name="currency"
                  placeholder={t(prefix + 'selectCurrency')}
                />
              </SettingBox>
              <div className="d-block d-lg-flex align-items-center my-3">
                <div className="col-lg-4 col pb-0 pb-lg-3 pb-lg-0 ps-0">
                  <span>{t(prefix + 'discount')}</span>
                </div>
                <div className="col px-0">
                  <div className="settingInputContainer">
                    <ButtonFormikTab
                      currValue={values.isDiscount}
                      items={isDiscountOption.map((i) => ({
                        label: t(i.label),
                        value: i.value,
                      }))}
                      name="isDiscount"
                    />
                  </div>
                </div>
              </div>

              <Modal show={showDiscountInfo} onHide={() => setShowDiscountInfo(false)} centered>
                <Modal.Header closeButton>
                  <Modal.Title>{t('creativeDetail-discountModalTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <p>{t('creativeDetail-discountModalDescription')}</p>
                  <ul className="mb-3">
                    <li>
                      <strong>{t('creativeDetail-discountModalFirstItem')}</strong> →{' '}
                      {t('creativeDetail-discountModalFirstItemDiscount')}
                    </li>
                    <li>
                      <strong>{t('creativeDetail-discountModalSecondItem')}</strong> →{' '}
                      {t('creativeDetail-discountModalSecondItemDiscount')}
                    </li>
                    <li>
                      <strong>{t('creativeDetail-discountModalThirdItem')}</strong> →{' '}
                      {t('creativeDetail-discountModalThirdItemDiscount')}
                    </li>
                  </ul>

                  <p className="text-muted small mt-3 mb-2">
                    {t('creativeDetail-discountModalExampleRates')}
                  </p>
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('creativeDetail-discountModalExampleClass')}</th>
                        <th>{t('creativeDetail-discountModalExampleOriginal')}</th>
                        <th>{t('creativeDetail-discountModalExampleDiscount')}</th>
                        <th>{t('creativeDetail-discountModalExampleFinal')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{t('creativeDetail-discountModalExampleClass')} 1 ($60)</td>
                        <td>$60</td>
                        <td>—</td>
                        <td>$60</td>
                      </tr>
                      <tr>
                        <td>{t('creativeDetail-discountModalExampleClass')} 2 ($50)</td>
                        <td>$50</td>
                        <td>10%</td>
                        <td>$45</td>
                      </tr>
                      <tr>
                        <td>{t('creativeDetail-discountModalExampleClass')} 3 ($40)</td>
                        <td>$40</td>
                        <td>20%</td>
                        <td>$32</td>
                      </tr>
                      <tr>
                        <td>{t('creativeDetail-discountModalExampleClass')} 4 ($30)</td>
                        <td>$30</td>
                        <td>20%</td>
                        <td>$24</td>
                      </tr>
                      <tr className="table-secondary fw-bold">
                        <td>{t('creativeDetail-discountModalExampleTotal')}</td>
                        <td>$180</td>
                        <td></td>
                        <td>$161</td>
                      </tr>
                    </tbody>
                  </table>
                </Modal.Body>
              </Modal>

              {values.isDiscount.toString() === 'true' && (
                <div className="d-block d-lg-flex align-items-center my-3">
                  <div className="col-lg-4 col pb-0 pb-lg-3 pb-lg-0 ps-0">
                    <span>{t(prefix + 'dpercent')}</span>
                    <InfoCircleFill
                      className="ms-2"
                      size={14}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setShowDiscountInfo(true)}
                    />
                  </div>
                  <div className="col px-0">
                    <div className="settingInputContainer">
                      <div className="setting-tag__discount-box">
                        <div className="setting-tag__discount-input-box">
                          <span>{t('creativeDetail-discount1')}</span>
                          <FormTextField name="discount1" id="discount1" className="py-0" />
                        </div>
                        <div className="setting-tag__discount-input-box">
                          <span>{t('creativeDetail-discount2')}</span>
                          <FormTextField name="discount2" id="discount2" className="py-0" />
                        </div>
                        <div className="setting-tag__discount-input-box">
                          <span>{t('creativeDetail-discount3')}</span>
                          <FormTextField name="discount3" id="discount3" className="py-0" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <SettingBox title="Streaming">
                <ButtonFormikTab
                  currValue={values.liveStreaming}
                  items={boolOption}
                  name="liveStreaming"
                />
              </SettingBox>
              {values.liveStreaming === 'true' && (
                <SettingBox title="Link">
                  <FormTextField name="liveStreamingLink" id="liveStreamingLink" className="py-0" />
                </SettingBox>
              )}
              {/* Rating: no backend support currently */}
              {/* <SettingBox title="Rating">
                <ButtonFormikTab currValue={values.isRating} items={boolOption} name="isRating" />
              </SettingBox>
              <SettingBox title="ratingType">
                <ButtonFormikTab
                  currValue={values.ratingType}
                  items={ratingOption}
                  name="ratingType"
                />
              </SettingBox> */}
              {/* isCanRecordScores: no backend support currently */}
              {/* <SettingBox title="Record">
                <ButtonFormikTab
                  currValue={values.isCanRecordScores}
                  items={boolOption}
                  name="isCanRecordScores"
                />
              </SettingBox> */}
              {/* {values.keywordTournamentType === 14 && (
                                <SettingBox title="matchPoints">
                                    <FormTextField
                                        name="matchPoints"
                                        id="matchPoints"
                                        className="py-0"
                                    />
                                </SettingBox>
                            )} */}
              <div className="d-block d-lg-flex align-items-center my-3">
                <div className="col-lg-4 col pb-0 pb-lg-3 pb-lg-0 ps-0">
                  <span>{t(prefix + 'officials')}</span>
                </div>
                <div className="col px-0">
                  <div className="w-100 d-flex flex-wrap flex-column flex-lg-row">
                    <div className="d-flex flex-column flex-lg-row align-items-end align-items-lg-start">
                      <CustomButton
                        className="d-flex justify-content-evenly align-items-center mr-0 mr-lg-2"
                        variant="outline-dark"
                        btnText={t(buttonPrefix + 'addOfficials')}
                        cb={handleOpenModal}
                      >
                        <AddIcon />
                      </CustomButton>
                    </div>
                    {officials.length ? (
                      officials.map((member) => (
                        <React.Fragment key={member.id}>
                          <MemberTournamentCard member={member} onClick={handleDeleteMember} />
                        </React.Fragment>
                      ))
                    ) : (
                      <div className="d-flex flex-column justify-content-center fw-lighter">
                        <p className="mb-0">{t(prefix + 'noOfficials') + '...'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="my-3">
              <h4 className="font-weight-bold">{t(prefix + 'default')}</h4>
            </div>
            <SettingBox title="tiebreaks">
              <GroupStandingsTieBreakerModalControl
                value={
                  values.useInheritedGroupStandingsTieBreakers
                    ? tournamentResolution.effective
                    : values.groupStandingsTieBreakers
                }
                onChange={(next) => setFieldValue('groupStandingsTieBreakers', next)}
                inheritChecked={values.useInheritedGroupStandingsTieBreakers}
                onInheritChange={(checked) =>
                  setFieldValue('useInheritedGroupStandingsTieBreakers', checked)
                }
                inheritLabelKey={getGroupStandingsInheritanceLabelKey(
                  Boolean(values.tour?.trim()),
                )}
                resolution={tournamentResolution}
                inheritScope="tournament"
                chainContextScope="tournament"
                scoringValue={
                  values.useInheritedTeamMatchupScoring
                    ? tournamentResolution.teamMatchupScoring
                    : values.teamMatchupScoring
                }
                onScoringChange={(next) => setFieldValue('teamMatchupScoring', next)}
                inheritScoringChecked={values.useInheritedTeamMatchupScoring}
                onInheritScoringChange={(checked) =>
                  setFieldValue('useInheritedTeamMatchupScoring', checked)
                }
                inheritScoringLabelKey={getTeamMatchupScoringInheritanceLabelKey(
                  Boolean(values.tour?.trim()),
                )}
                openSignal={tieBreakerOpenSignal}
              />
            </SettingBox>
            <div className="row mb-5">
              {/* stateOfClass currently doesn't do anything in the app */}
              {/* <SettingBox title="State">
                <ButtonFormikTab
                  currValue={t(values.stateOfClass)}
                  items={stateClassOption.map(t) as string[]}
                  name="stateOfClass"
                />
              </SettingBox> */}
              <SettingBox title="Ball">
                <ReactSelectForm labelPdd="pb-0" options={ballOption} name="keywordBall" />
              </SettingBox>
              <SettingBox title="Surface">
                <ReactSelectForm labelPdd="pb-0" options={surfaceOption} name="keywordSurface" />
              </SettingBox>
              {/* isTicketSales doesn't appear to be used anywhere currently */}
              {/* <SettingBox title="Ticket">
                <ButtonFormikTab
                  currValue={t(values.isTicketSales)}
                  items={ticketSalesOption.map(t) as string[]}
                  name="isTicketSales"
                />
              </SettingBox> */}
              <div className="d-block d-lg-flex align-items-center my-3">
                <div className="col-lg-4 col pb-0 pb-lg-3 pb-lg-0 ps-0">
                  <div className="d-flex align-items-center gap-2">
                    <span>{t(prefix + 'showbtn')}</span>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          {t('creativeDetail-showSalesButtonTooltip')}
                        </Tooltip>
                      }
                    >
                      <Button variant="link" className="p-0">
                        <InfoIcon width={24} height={24} />
                      </Button>
                    </OverlayTrigger>
                  </div>
                </div>
                <div className="col px-0">
                  <div className="settingInputContainer">
                    <ButtonFormikTab
                      currValue={values.isShowSalesButton}
                      items={boolOption}
                      name="isShowSalesButton"
                    />
                  </div>
                </div>
              </div>

              <SettingBox
                title={
                  values.keywordTournamentType === 14 ? 'minteams' : 'MinPlayers'
                }
              >
                <div className="w-25">
                  <FormTextField name="minPlayers" id="minPlayers" className="py-0" />
                </div>
              </SettingBox>
              <SettingBox
                title={
                  values.keywordTournamentType === 14 ? 'maxteams' : 'MaxPlayers'
                }
              >
                <div className="w-25">
                  <FormTextField name="maxPlayers" id="maxPlayers" className="py-0" />
                </div>
              </SettingBox>
              <SettingBox title="minroster" tooltipKey="minRosterTooltip">
                <div className="w-25">
                  <FormTextField name="minTeamRoster" id="minTeamRoster" className="py-0" />
                </div>
              </SettingBox>
              <SettingBox title="maxroster" tooltipKey="maxRosterTooltip">
                <div className="w-25">
                  <FormTextField name="maxTeamRoster" id="maxTeamRoster" className="py-0" />
                </div>
              </SettingBox>
              <p className="text-muted small mb-3">
                {t(prefix + 'rosterDefaultsDisclaimer')}
              </p>
              <SettingBox title="isDupr">
                <ButtonFormikTab currValue={values.isDupr} items={boolOption} name="isDupr" />
              </SettingBox>
              <SettingBox title="requiresLicense">
                <ButtonFormikTab
                  currValue={values.requiresLicense}
                  items={boolOption}
                  name="requiresLicense"
                />
              </SettingBox>

              <SettingBox title="PaidClass">
                <ButtonFormikTab
                  currValue={values.isPaidClass}
                  items={boolOption}
                  name="isPaidClass"
                />
              </SettingBox>

              {getBool(values.isPaidClass) && (
                <>
                  <SettingBox title="UseVAT">
                    <ButtonFormikTab currValue={values.useVat} items={boolOption} name="useVat" />
                  </SettingBox>
                  {getBool(values.useVat) && (
                    <SettingBox title="VATpercentage">
                      <div className="w-25">
                        <FormTextField name="vat" id="vat" className="py-0" />
                      </div>
                    </SettingBox>
                  )}
                  <SettingBox title="VAT">
                    <div className="w-25">
                      <FormTextField placeholder="€0" name="price" id="price" className="py-0" />
                    </div>
                  </SettingBox>
                </>
              )}
            </div>
          </Form>
        )}}
      </Formik>
    </div>
  );
};
